import React, {
  PropsWithChildren,
  ReactNode,
  useState,
  useRef,
  CSSProperties,
  useEffect,
  useMemo,
} from "react";

import { createPortal } from "react-dom";

import { useHoverOutside } from "project-config/hooks/useHoverOutside";
import { useClickOutside } from "project-config/hooks/useClickOutside";

import { FlattenSimpleInterpolation } from "styled-components";

import { PlacementType } from "../../constants/placement";
import DropdownArrowIcon from "../../assets/svg/dropdown-arrow";

import { DropdownWrapper, DropdownContent } from "./atom";

export function Dropdown({
  open = false,
  placement = "bottom",
  children,
  trigger = "hover",
  closeOnclick = false,
  closeOnClickOutside = true,
  closeOnHoverOutside = true,
  dropdownRender,
  className = "",
  style,
  dropdownContentStyle,
  popupWidth = "max-content",
  wrapperBackground,
  wrapperActiveBackground,
  showArrow = false,
  arrowRight = "10px",
  offsetX = "0px",
  offsetY = "0px",
  customCSS,
  dropdownContentCSS,
  wrapperActiveCSS,
  fixed,
  disabled,
  onClick,
  onVisibleChange = (visible) => {},
  ...props
}: DropdownProps) {
  const [_open, setOpen] = useState(open);
  const [basicLeft, setBasicLeft] = useState(0);
  const [basicTop, setBasicTop] = useState(0);
  const [basicRight, setBasicRight] = useState(0);
  const [basicBottom, setBasicBottom] = useState(0);
  const [dropdownContentWidth, setDropdownContentWidth] = useState(0);
  const dropdownWrapperRef = useRef(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const isSelect = className.includes("task-eva-select");
  const handleClick = (e) => {
    if (open || disabled) return;
    onClick?.(e);
    if (trigger === "click" || closeOnclick) {
      setOpen(!_open);
    }
  };
  const handleHover = () => {
    if (trigger === "click") return;
    setOpen(true);
  };
  const handleMouseOut = () => {
    if (!closeOnHoverOutside) return;
    setOpen(false);
  };
  useEffect(() => {
    setOpen(open);
  }, [open]);
  useEffect(() => {
    onVisibleChange(_open);
  }, [_open]);
  useHoverOutside(
    [dropdownContentRef, dropdownWrapperRef],
    () => closeOnHoverOutside && setOpen(false),
    closeOnHoverOutside && _open,
    [_open, closeOnHoverOutside, dropdownContentRef, dropdownWrapperRef]
  );
  useClickOutside(
    [dropdownContentRef, dropdownWrapperRef],
    () => closeOnClickOutside && setOpen(false),
    closeOnClickOutside && _open,
    [_open, closeOnClickOutside, dropdownContentRef, dropdownWrapperRef]
  );
  useEffect(() => {
    if (!fixed) return;
    if (!open && !_open && !dropdownWrapperRef.current) return;
    const el = dropdownWrapperRef.current;
    const { left, top, right, bottom } = el.getBoundingClientRect();
    setBasicLeft(left);
    setBasicTop(top);
    setBasicRight(right);
    setBasicBottom(bottom);
  }, [dropdownWrapperRef.current?.getBoundingClientRect(), _open, open, fixed]);
  useEffect(() => {
    if (!dropdownContentRef.current || !fixed) return;
    setDropdownContentWidth(
      dropdownContentRef.current.offsetWidth ||
        dropdownContentRef.current.clientWidth
    );
  }, [
    dropdownContentRef.current?.offsetWidth,
    dropdownContentRef.current?.clientWidth,
    basicLeft,
    basicRight,
    basicTop,
    basicBottom,
    fixed,
  ]);

  const renderDropdownContent = useMemo(() => {
    return disabled ? null : (
      <DropdownContent
        ref={dropdownContentRef}
        open={_open}
        width={isSelect ? `${basicRight - basicLeft}px` : popupWidth}
        placement={placement}
        fixed={fixed}
        data-placement={placement}
        offsetX={offsetX}
        offsetY={offsetY}
        basicLeft={basicLeft}
        basicTop={basicTop}
        basicRight={basicRight}
        basicBottom={basicBottom}
        dropdownContentWidth={dropdownContentWidth}
        style={dropdownContentStyle}
        dropdownContentCSS={dropdownContentCSS}
        className={`task-eva-dropdown-content ${fixed ? "fixed" : ""}`}
        onMouseLeave={handleMouseOut}
      >
        {dropdownRender}
      </DropdownContent>
    );
  }, [
    disabled,
    placement,
    _open,
    dropdownContentRef,
    isSelect,
    basicRight,
    basicLeft,
    popupWidth,
    fixed,
    dropdownRender,
    basicTop,
    basicBottom,
    dropdownContentWidth,
    dropdownContentStyle,
    dropdownContentCSS,
  ]);

  return (
    <DropdownWrapper
      ref={dropdownWrapperRef}
      style={style}
      active={_open}
      showArrow={showArrow}
      arrowRight={arrowRight}
      wrapperBackground={wrapperBackground}
      wrapperActiveBackground={wrapperActiveBackground}
      customCSS={customCSS}
      wrapperActiveCSS={wrapperActiveCSS}
      className={`task-eva-dropdown ${className}`}
      onMouseOver={handleHover}
      onClick={handleClick}
      {...props}
    >
      {children}
      {fixed
        ? createPortal(renderDropdownContent, document.body)
        : renderDropdownContent}
      {showArrow && <DropdownArrowIcon />}
    </DropdownWrapper>
  );
}

export type PopupTriggerType = "click" | "hover";

export type DropdownProps = PropsWithChildren<{
  open?: boolean;
  className?: string;
  style?: CSSProperties;
  dropdownContentStyle?: CSSProperties;
  popupWidth?: string;
  wrapperBackground?: string;
  wrapperActiveBackground?: string;
  customCSS?: any;
  dropdownContentCSS?: any;
  wrapperActiveCSS?: any;
  showArrow?: boolean;
  arrowRight?: string;
  trigger?: PopupTriggerType;
  closeOnclick?: boolean;
  closeOnClickOutside?: boolean;
  closeOnHoverOutside?: boolean;
  placement?: PlacementType;
  offsetY?: string;
  offsetX?: string;
  fixed?: boolean;
  disabled?: boolean;
  onVisibleChange?: (visibale: boolean) => void;
  dropdownRender?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  [key: string]: any;
}>;
